
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { ITableConfig } from "@/models/tables";
import { DataModules } from "@/store/modules/data";
import { CreateElement } from "vue";
import {
  ISegmentsProviderData,
  ISegmentsProviderMethods
} from "@/models/providers/segments";
import { Sorters } from "@/data/table";
import { IProvider } from "@/models/providers";
import _ from "@/boot/lodash";
import { IMenuRoute } from "@/models/routes";
import { ISegmentTypes } from "@/data/enums/segments";
import { ISegment } from "@/models/segments";
import { ActionTypes } from "@/data/enums";

@Component
export default class SegmentsProvider extends Mixins(FilteringSortingTableStore)
  implements IProvider {
  // Props

  @Prop({ required: true })
  public segmentType!: ISegmentTypes;

  @Prop({ default: "CCP" })
  public declare id: string;

  @Prop({ default: 0 })
  public declare limit: number;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ required: true })
  public declare redirectRouteName: string;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Data

  public dataModule = DataModules.SEGMENTS;
  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;
  public isProcessing = false;
  public isProcessingDelete = false;
  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "created_at",
      direction: Sorters.DESCENDING
    },
    filters: []
  };

  // Computed

  public get segmentFilter() {
    return { "filter[type]": this.segmentType };
  }

  public get hasProducts(): boolean {
    return !!this.table.data.length;
  }

  public get canShowMore(): boolean {
    return this.table.total > this.table.data.length;
  }

  public get canCreate() {
    return this.$userCan("create_segment");
  }

  public get canUpdate() {
    return this.$userCan("update_segment");
  }

  public get canDelete() {
    return this.$userCan("delete_segment");
  }

  public get contentAvailable() {
    return this.$userCan("list_segments");
  }

  public get routes(): IMenuRoute[] {
    return _.map(this.table.data, segment => ({
      label: segment.name,
      to: {
        name: this.redirectRouteName,
        params: {
          segmentId: segment.id
        }
      }
    }));
  }

  // Methods

  public loadData = this.getData;

  public getStoreParams(params: any): Promise<any> {
    // Overrides mixin method
    return this.$_.merge(
      {
        params: {
          ...this.segmentFilter,
          ...this.manualFilters
        }
      },
      params
    );
  }

  public reloadData() {
    return this.getData({ ...this.config, ignoreStored: true });
  }

  public openManageSegmentModal(segmentId: ISegment["id"]): void {
    if (segmentId ? this.canUpdate : this.canCreate) {
      this.$store.dispatch(
        `data/${DataModules.SEGMENTS}/openManageSegmentModal`,
        {
          props: this.$_.merge(
            {
              segmentType: this.segmentType,
              actionType: segmentId ? ActionTypes.UPDATE : ActionTypes.CREATE
            },
            segmentId ? { segmentId } : {}
          )
        }
      );
    }
  }

  public async deleteSegment(segmentId: ISegment["id"]) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<void>(async resolve => {
      const confirm = await this.$store.dispatch("ui/open/confirmModal", {
        config: {
          props: {
            autoClose: false,
            confirmButtonText: this.$t("_action.delete"),
            message: this.$t("_sentence.delete_confirmation", {
              object: (this.$t("_.segment") as string).toLowerCase()
            })
          },
          events: {
            confirmed: async () => {
              await this.onDeleteConfirmed(segmentId);
              confirm.close();
              resolve();
            }
          }
        }
      });
    });
  }

  public onDeleteConfirmed(segmentId: ISegment["id"]): Promise<any> {
    this.isProcessingDelete = true;
    return this.$store
      .dispatch(`data/${this.dataModule}/remove`, {
        id: segmentId
      })
      .then(() => {
        this.$store.dispatch("toast/show", {
          message: this.$t("_sentence.segment_successfully_removed")
        });
      })
      .catch(error => {
        this.$store.dispatch("api/handleError", error);
      })
      .finally(() => {
        this.isProcessingDelete = false;
      });
  }

  // Life-cycle Methods

  public created() {
    this.getData();
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.getData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $segmentsData: this.providerData(),
        $segmentsMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("segmentsProviderMethods")
  public get providerMethods(): ISegmentsProviderMethods {
    return {
      onPageChange: this.onPageChange,
      reloadData: this.reloadData,
      openManageSegmentModal: this.openManageSegmentModal,
      deleteSegment: this.deleteSegment
    };
  }

  @Provide("segmentsProviderData")
  public providerData(): ISegmentsProviderData {
    return {
      contentAvailable: this.contentAvailable,
      canCreate: this.canCreate,
      canUpdate: this.canUpdate,
      canDelete: this.canDelete,
      segments: this.table.data,
      hasRequestError: this.requestError,
      isLoading: this.table.loading,
      isReloading: this.table.loading,
      limit: this.table.limit,
      page: this.table.page,
      routes: this.routes,
      total: this.table.total
    };
  }
}
